import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import makeStyles from '@mui/styles/makeStyles'
import React from 'react'
import { Link } from 'react-router-dom'
import { getBlogArticleUrl } from '../../urls'
import { useTranslation } from 'react-i18next'
import FormattedDate from '@components/FormattedDate'

const useStyles = makeStyles({
  root: {
    position: 'relative',
    border: '1px solid #d8d8d8',
    '& a': {
      textDecoration: 'none',
    },
    '&:hover $title': {
      textDecoration: 'underline',
      color: '#020c6d',
    },
    '&:hover $icons': {
      opacity: 1,
    },
  },
  image: {
    position: 'absolute',
    left: 0,
    top: 0,
    maxWidth: '100%',
    maxHeight: '100%',
  },
  title: {
    height: 70,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-moz-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    '-moz-box-orient': 'vertical',
  },
  annotation: {
    marginTop: 10,
    marginBottom: 24,
    fontSize: 14,
    lineHeight: '20px',
    height: 60,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-moz-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    '-moz-box-orient': 'vertical',
    color: '#9B9E9E',
  },
  author: {
    color: '#222',
  },
  icons: {
    opacity: 0,
    position: 'absolute',
    marginTop: 6,
    display: 'flex',
    flexDirection: 'column',
    width: 28,
    right: 20,
    top: 10,
    transition: 'opacity .3s ease',
  },
  icon: {
    marginBottom: 6,
    border: '1px solid #d8d8d8',
    backgroundColor: '#fff',
    transition: 'transform .1s ease',
    '&:hover': {
      backgroundColor: '#fff',
      transform: 'scale(1.05)',
    },
    '& svg': {
      width: 14,
      height: 14,
    },
    '&[data-active=true]': {
      color: '#F97B62',
    },
  },
  imgPlaceholder: {
    padding: '29%',
    position: 'relative',
  },
  dateAdded: {
    paddingTop: 10,
    color: '#656969',
    fontSize: 10,
    paddingBottom: 10,
  },
})
const LIKE = 5
const DISLIKE = 1

interface IArticle {
  pk?: number
  slug: string
  mediumThumbnailWebp?: string
  smallThumbnailWebp?: string
  mediumThumbnail?: string
  smallThumbnail?: string
  image?: {
    url: string
  }
  title: string
  dateAdded: string
  annotation: string
  authorName?: string
  userMeasure?: number
}

interface IProps {
  article: IArticle
  blogSlug: string
  url?: string

  updateMeasure(articlePk: number, measure: number): Promise<void>

  onAddArticle?(e: any): Promise<void> | void
}

const ArticleCard: React.FC<IProps> = ({ article, blogSlug, updateMeasure, onAddArticle, url }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const matchesMd = useMediaQuery(theme.breakpoints.down('sm'))

  const handleClickLink = async e => {
    if (onAddArticle) await onAddArticle(e)
  }

  return (
    <div className={classes.root}>
      <Link to={url || getBlogArticleUrl(blogSlug, article.slug)} onClick={handleClickLink}>
        <div className={classes.imgPlaceholder}>
          <img
            className={classes.image}
            src={(matchesMd ? article.mediumThumbnail : article.smallThumbnail) || article.image?.url}
            alt={article.title}
          />
        </div>
        <div className='pb-20 px-15'>
          <div className={classes.dateAdded}>
            <FormattedDate value={article.dateAdded} />
          </div>
          <div>
            <Typography className={classes.title} variant='h5'>
              {article.title}
            </Typography>
          </div>

          <div className={classes.annotation}>{article.annotation}</div>
          <div className={classes.author}>
            {t('articleByAuthor', { defaultValue: 'By {{authorName}}', authorName: article.authorName })}
          </div>
        </div>
      </Link>
      {!onAddArticle && (
        <div className={classes.icons}>
          <Tooltip title={t('tooltip.moreLikeThis', 'More like this')} enterDelay={300}>
            <IconButton
              size='small'
              className={classes.icon}
              onClick={e => updateMeasure(article.pk, LIKE)}
              data-active={article.userMeasure > 3}
            >
              <ThumbUpOutlinedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('tooltip.lessLikeThis', 'Less like this')} enterDelay={300}>
            <IconButton
              size='small'
              className={classes.icon}
              onClick={e => updateMeasure(article.pk, DISLIKE)}
              data-active={article.userMeasure < 3 && article.userMeasure > 0}
            >
              <ThumbDownAltOutlinedIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export default ArticleCard
